import {ModuleComponent} from "../../js/module-component";

/**
 * Hero tipo espacio:
 *  - Fondo: color sólido + campo de estrellas (canvas) en varias capas de
 *    profundidad, con twinkle y parallax por profundidad (scroll + cursor).
 *  - Contenido: parallax inverso (profundidad) + fundido al salir.
 *  - Entrada escalonada de textos/imagen.
 *  Todo con requestAnimationFrame y respetando prefers-reduced-motion.
 */
export class ModHeroAnimation extends ModuleComponent{
    ready(){
        super.ready();

        this.el = this.$root.get(0);
        if(!this.el) return;
        this.item = this.el.querySelector(`.web-mod-hero-animation__item`);
        if(!this.item) return;

        this.canvas = this.el.querySelector(`.web-mod-hero-animation__stars`);
        this.ctx = this.canvas ? this.canvas.getContext(`2d`) : null;
        this.inner = this.el.querySelector(`.web-mod-hero-animation__item-inner`);

        this.reduceMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`).matches;

        // Estado del parallax (compartido por estrellas y contenido).
        this.mx = 0; this.my = 0;   // puntero suavizado (-1..1)
        this.tmx = 0; this.tmy = 0; // puntero objetivo
        this.prog = 0;              // progreso de scroll (-1..1)

        // Starfield inicial (estático).
        if(this.canvas){
            this.buildStars();
            this.drawStars(0);
        }

        // Progressive enhancement.
        this.el.classList.add(`is-anim-ready`);

        // Entrada al cargar (above-the-fold).
        const reveal = () => this.item.classList.add(`is-revealed`);
        if(this.reduceMotion || !(`requestAnimationFrame` in window)){
            reveal();
        }else{
            requestAnimationFrame(() => requestAnimationFrame(reveal));
            setTimeout(reveal, 700);
        }

        // Visibilidad (gating del bucle).
        this.visible = true;
        if(`IntersectionObserver` in window){
            this.io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        this.visible = entry.isIntersecting;
                        if(this.visible) this.startLoop();
                    });
                },
                {threshold: 0}
            );
            this.io.observe(this.item);
        }

        // Reconstruye estrellas al redimensionar.
        this.onResize = () => { this.buildStars(); this.drawStars(this._t || 0); };
        window.addEventListener(`resize`, this.onResize, {passive: true});

        if(this.reduceMotion) return;

        this.usePointer = window.matchMedia(`(min-width: 992px) and (pointer: fine)`).matches;
        if(this.usePointer){
            this.onMove = (ev) => {
                const r = this.el.getBoundingClientRect();
                this.tmx = ((ev.clientX - r.left) / r.width - 0.5) * 2;
                this.tmy = ((ev.clientY - r.top) / r.height - 0.5) * 2;
            };
            this.onLeave = () => { this.tmx = 0; this.tmy = 0; };
            this.el.addEventListener(`mousemove`, this.onMove, {passive: true});
            this.el.addEventListener(`mouseleave`, this.onLeave, {passive: true});
        }

        this.loopBound = (t) => this.loop(t);
        this.startLoop();
    }

    /** Estrellas con profundidad (z): cercanas grandes/brillantes, lejanas pequeñas. */
    buildStars(){
        if(!this.canvas) return;
        const r = this.item.getBoundingClientRect();
        const w = Math.max(1, r.width);
        const h = Math.max(1, r.height);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this._dpr = dpr;
        this.canvas.width = Math.round(w * dpr);
        this.canvas.height = Math.round(h * dpr);

        const count = Math.max(40, Math.min(340, Math.round((w * h) / 9000)));
        const stars = [];
        for(let i = 0; i < count; i++){
            const z = 0.25 + Math.random() * 0.75; // profundidad (1 = cerca)
            stars.push({
                // margen extra fuera del lienzo para que el parallax no deje bandas
                x: -0.06 + Math.random() * 1.12,
                y: -0.06 + Math.random() * 1.12,
                z: z,
                r: 0.5 + z * 1.3,          // cercanas más grandes
                a: 0.22 + z * 0.5,         // cercanas más brillantes
                sp: 0.4 + Math.random() * 1.3,
                ph: Math.random() * Math.PI * 2,
                blue: Math.random() > 0.82
            });
        }
        this.stars = stars;
    }

    /** Pinta el starfield con twinkle + parallax por profundidad. */
    drawStars(t){
        const ctx = this.ctx;
        if(!ctx || !this.stars) return;
        const w = this.canvas.width;
        const h = this.canvas.height;
        const dpr = this._dpr || 1;

        // Desplazamientos base (px CSS) que luego se escalan por profundidad.
        const scrollAmp = 52;
        const pxAmp = 22;
        const pyAmp = 14;
        const baseX = this.mx * pxAmp;
        const baseY = (-this.prog * scrollAmp) + (this.my * pyAmp);

        ctx.clearRect(0, 0, w, h);
        for(let i = 0; i < this.stars.length; i++){
            const s = this.stars[i];
            const tw = this.reduceMotion ? 1 : (0.65 + 0.35 * Math.sin(t * 0.001 * s.sp + s.ph));
            const ox = baseX * s.z * dpr;
            const oy = baseY * s.z * dpr;
            ctx.globalAlpha = Math.max(0, Math.min(1, s.a * tw));
            ctx.beginPath();
            ctx.arc(s.x * w + ox, s.y * h + oy, s.r * dpr, 0, 6.2832);
            ctx.fillStyle = s.blue ? `#cfe0ff` : `#ffffff`;
            ctx.fill();
        }
        ctx.globalAlpha = 1;
    }

    /** Bucle único (estrellas + contenido) mientras el hero está visible. */
    startLoop(){
        if(this._loop || this.reduceMotion) return;
        this._loop = requestAnimationFrame(this.loopBound);
    }

    loop(t){
        this._t = t;

        // Suavizado del puntero.
        this.mx += (this.tmx - this.mx) * 0.08;
        this.my += (this.tmy - this.my) * 0.08;

        // Progreso de scroll.
        const r = this.item.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const center = r.top + r.height / 2;
        this.prog = Math.max(-1, Math.min(1, (center - vh / 2) / ((vh + r.height) / 2)));

        // Estrellas.
        this.drawStars(t);

        // Contenido: profundidad inversa al puntero + drift y fundido al salir.
        if(this.inner){
            const inX = -this.mx * 10;
            const inY = (-this.my * 8) + (this.prog * 26);
            this.inner.style.transform = `translate3d(${inX.toFixed(2)}px, ${inY.toFixed(2)}px, 0)`;
            this.inner.style.opacity = String(Math.max(0, 1 - Math.max(0, this.prog) * 0.9));
        }

        this._loop = this.visible ? requestAnimationFrame(this.loopBound) : null;
    }
}
